import React from 'react';
import moment from 'moment';
import {
  Box,
  Typography,
  Divider,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  PeopleIcon,
  BuildingsIcon,
  CalendarIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';

const HouseholdCard = ({
  household,
  onLeaseholderClick,
  showDivider = true,
}) => {
  const handleLeaseholderClick = () => {
    if (onLeaseholderClick) {
      onLeaseholderClick(household.primaryLeaseholder);
    }
  };

  const formatDateRange = () => {
    if (!household.moveInDate) return '';

    const moveInDate = moment(household.moveInDate).format('MMM DD, YYYY');

    if (household.moveOutDate) {
      const moveOutDate = moment(household.moveOutDate).format('MMM DD, YYYY');
      return `${moveOutDate} (Scheduled Move-out Date)`;
    }

    return `${moveInDate} (Scheduled Move-in Date)`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'approved';
      case 'converted to resident':
        return 'approved';
      case 'in process':
        return 'inProgress';
      case 'denied':
        return 'denied';
      case 'waitlist':
        return 'waitlist';
      case 'canceled':
        return 'canceled';
      case 'new portal app':
        return 'pending';
      case 'abandoned':
        return 'closed';
      default:
        return 'canceled';
    }
  };

  const getBedBathDisplay = () => {
    return `${household.bedrooms} Bed / ${household.bathrooms} Bath`;
  };

  return (
    <Box>
      <Box sx={{ py: 3, px: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs>
            {/* Primary Leaseholder */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PeopleIcon color="inherit" />
              <Typography
                variant="bodyMedium"
                fontWeight="medium"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={
                  onLeaseholderClick ? handleLeaseholderClick : undefined
                }
              >
                {household.primaryLeaseholder.firstName}{' '}
                {household.primaryLeaseholder.lastName}
              </Typography>
            </Box>

            {/* Unit info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BuildingsIcon color="secondary" />
              <Typography variant="bodySmall" color="text.secondary">
                Unit {household.unitNumber}
              </Typography>
            </Box>

            {/* Bed/Bath info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {/* Theres no bed icon in the component-library */}
              <i
                className="icon et-bed"
                style={{
                  color: '#4d73ff',
                  width: '20px',
                  height: '20px',
                  fontSize: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
              <Typography variant="bodySmall" color="text.secondary">
                {getBedBathDisplay()}
              </Typography>
            </Box>

            {/* Date range */}
            {formatDateRange() && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="secondary" />
                <Typography variant="bodySmall" color="text.secondary">
                  {formatDateRange()}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs="auto">
            <BadgeStatus
              status={getStatusColor(household.status)}
              size="small"
              variant="filled"
            >
              {household.status}
            </BadgeStatus>
          </Grid>
        </Grid>
      </Box>

      {showDivider && <Divider />}
    </Box>
  );
};

export default HouseholdCard;
